import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy, limit } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './Firebase';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import './PromptEditor.css'; // Import CSS file for animations

const ADMIN_UIDS = [
  'bTGBBpeYPmPJonItYpUOCYhdIlr1',
  'yvsWRZwjTQecvGap3pGXWNGHoTp2',
  'lpwCpZkPk2h1ZWrESgkyXPUXEPQ2',
];
let originalPromptText = '';
let originalTagText = '';

const PromptEditor = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editTag, setEditTag] = useState('');
  const [editFullText, setEditFullText] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // New state for search
  const [isUploading, setIsUploading] = useState(false); // New state for file upload

  // Check authentication and admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setUser(user);
      if (ADMIN_UIDS.includes(user.uid)) {
        console.log('Admin user authenticated:', user.uid);
        setIsAdmin(true);
        fetchUsers();
      } else  {
        // Not admin: skip user select, set selectedUser to current user
        console.log('Non-admin user authenticated:', user.uid);
        setIsAdmin(false);
        setSelectedUser(user.uid);
      } 
    });
    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Fetch all users from genai collection
  const fetchUsers = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      console.log(`Found ${usersSnapshot.docs.length} users`);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error fetching users: ' + error.message);
    }
  };

  // Filter prompts based on search query
  const filteredPrompts = prompts.filter(p => 
    p.tag.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch prompts for selected user
  useEffect(() => {
    if (!selectedUser) return;
    
    const fetchPrompts = async () => {
      try {
        console.log('Fetching prompts for user:', selectedUser);
        const promptsCollection = collection(db, 'genai', selectedUser, 'prompts');
        const qPrompts = query(promptsCollection, orderBy('modifiedDateTime', 'desc'), limit(1000));
        const promptsSnapshot = await getDocs(qPrompts);
        setPrompts(promptsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        console.log(`Found ${promptsSnapshot.docs.length} prompts for user ${selectedUser}`);
      } catch (error) {
        console.error('Error fetching prompts:', error);
        alert('Error fetching prompts: ' + error.message);
      }
    };
    
    fetchPrompts();
  }, [selectedUser]);

  // When prompt selected, load its data
  useEffect(() => {
    if (!selectedPrompt) {
      setEditTag('');
      setEditFullText('');
      return;
    }
    setEditTag(selectedPrompt.tag || '');
    originalTagText = selectedPrompt.tag || '';
    setEditFullText(selectedPrompt.fullText || '');
    originalPromptText = selectedPrompt.fullText || '';
  }, [selectedPrompt]);

  // Save prompt and trigger embedding
  const handleSave = async () => {
    if (!selectedUser) {
      alert('Authentication required to save prompts');
      return;
    }
    
    if (!editTag.trim() || !editFullText.trim()) {
      alert('Please enter a tag and prompt text.');
      return;
    }
    
    setSaving(true);
    try {
      const promptsCollection = collection(db, 'genai', selectedUser, 'prompts');
      const archiveCollection = collection(db, 'genai', selectedUser, 'archivedPrompts');
      const currentDateTime = new Date();
      let docId = '';
      let promptData = {
        tag: editTag,
        fullText: editFullText,
        modifiedDateTime: currentDateTime,
        size: editFullText.length,
      };
      if (!selectedPrompt) {
        // Add new prompt
        promptData.createdDateTime = currentDateTime;
        const newDocRef = await addDoc(promptsCollection, promptData);
        docId = newDocRef.id;
      } else {
        // Update existing prompt
        const docRef = doc(db, 'genai', selectedUser, 'prompts', selectedPrompt.id);
        await updateDoc(docRef, promptData);
        docId = selectedPrompt.id;
        // Also add to archive
        // Also add to archive originalPromptText
        await addDoc(archiveCollection, {
          originalTag: originalTagText,
          tag: editTag,
          fullText: originalPromptText,
          modifiedDateTime: currentDateTime,
          archivedDateTime: currentDateTime,
          originalPromptId: docId,
        });
      }
      // Trigger vector embedding
      fetch(`${process.env.REACT_APP_GENAI_API_URL}embed-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_id: docId, uid: selectedUser }),
      });
      alert('Prompt saved!');
      setSelectedPrompt(null);
      setEditTag('');
      setEditFullText('');
      // Refresh prompts
      const promptsCollection2 = collection(db, 'genai', selectedUser, 'prompts');
      const qPrompts2 = query(promptsCollection2, orderBy('modifiedDateTime', 'desc'), limit(1000));
      const promptsSnapshot2 = await getDocs(qPrompts2);
      setPrompts(promptsSnapshot2.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      alert('Error saving prompt: ' + error.message);
    }
    setSaving(false);
  };

  // Function to trigger embedding process
  const embedPrompt = async (docId) => {
    try {
      await fetch(`${process.env.REACT_APP_GENAI_API_URL}embed-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_id: docId, uid: selectedUser }),
      });
      console.log('Embedding triggered for document:', docId);
    } catch (error) {
      console.error('Error triggering embedding:', error);
    }
  };

  // Function to fetch prompts (for refreshing after upload)
  const fetchPrompts = async (uid) => {
    try {
      console.log('Fetching prompts for user:', uid);
      const promptsCollection = collection(db, 'genai', uid, 'prompts');
      const qPrompts = query(promptsCollection, orderBy('modifiedDateTime', 'desc'), limit(1000));
      const promptsSnapshot = await getDocs(qPrompts);
      setPrompts(promptsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      console.log(`Found ${promptsSnapshot.docs.length} prompts for user ${uid}`);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      alert('Error fetching prompts: ' + error.message);
    }
  };

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#f8f8f8', position: 'fixed', top: 0, left: 0, zIndex: 2000 }}>
      <div style={{ display: 'flex', height: '100%' }}>
        <div style={{ width: '30%', borderRight: '1px solid #ccc', padding: 16, overflowY: 'auto' }}>
            {/* Only show user select if admin */}
            {isAdmin && (
              <>
                <h3>User</h3>
                <select
                    value={selectedUser}
                    onChange={e => {
                        setSelectedUser(e.target.value);
                        setSelectedPrompt(null);
                        setEditTag('');
                        setEditFullText('');
                    }}
                    style={{ width: '100%', fontSize: 16 }}
                >
                    <option value=''>Select User</option>
                    {users.map(u => (
                        <option
                            key={u.uid}
                            value={u.uid}
                        >
                            {u.email}
                        </option>
                    ))}
                </select>
                {users.length > 0 && !selectedUser && (() => {
                    const defaultUser = users.find(u => u.email === user.email);
                    if (defaultUser) {
                        setSelectedUser(defaultUser.uid);
                    }
                    return null;
                })()}
              </>
            )}
            <span style={{ marginTop: 24, fontSize: 22 }}><strong>Prompts</strong></span> &nbsp;&nbsp;&nbsp;
            <button onClick={onClose} style={{ fontSize: 16, background: 'orange', color: 'black', padding: '4px 4px', border: 'none', borderRadius: 4 }}>Go Back</button>
            <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
              <button style={{ flex: '1' }} onClick={() => setSelectedPrompt(null)}>+ New Prompt</button>
              <div style={{ position: 'relative', flex: '1' }}>
                {isUploading && (
                  <FaSpinner className="spinning" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    animation: 'spin 1s linear infinite',
                    zIndex: 1
                  }} />
                )}
                <input
                  type="file"
                  style={{ 
                    width: '100%',
                    padding: '2px', 
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('uid', selectedUser);
                      formData.append('filename', file.name);
                      try {
                        setIsUploading(true); // Show spinning wheel
                        const response = await fetch(`${process.env.REACT_APP_GENAI_API_URL}md`, {
                          method: 'POST',
                          body: formData
                        });

                        if (!response.ok) {
                          const errorData = await response.json();
                          throw new Error(errorData.error || 'Failed to upload document.');
                        }
                        const data = await response.json();
                        console.log('Upload Response:', data);
                        const genaiCollection = collection(db, 'genai', selectedUser, 'prompts');
                        const currentDateTime = new Date();
                        const promptSize = data.markdown.length;
                        console.log('Adding new prompt from uploaded document');
                        const newDocRef = await addDoc(genaiCollection, {
                          tag: file.name,
                          fullText: data.markdown,
                          createdDateTime: currentDateTime,
                          modifiedDateTime: currentDateTime,
                          size: promptSize
                        });
                        const docId = newDocRef.id;
                        console.log('Document ID:', docId);
                        embedPrompt(docId);
                        fetchPrompts(selectedUser);
                        setIsUploading(false); // Hide spinning wheel
                        alert('Document uploaded successfully!');
                      } catch (error) {
                        console.error('Error uploading document:', error);
                        alert(`Error: ${error.message}`);
                        setIsUploading(false);
                      }
                    }
                  }}
                />
              </div>
            </div>
            {/* New Search Box */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, position: 'relative' }}>
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px 12px 8px 32px', 
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
              <FaSearch style={{ position: 'absolute', left: '10px', color: '#666' }} />
            </div>
            
            {/* Show count of filtered prompts */}
            {searchQuery && (
              <div style={{ marginBottom: 8, fontSize: '14px', color: '#666' }}>
                Found {filteredPrompts.length} {filteredPrompts.length === 1 ? 'prompt' : 'prompts'}
              </div>
            )}
            
            <ul style={{ listStyle: 'none', padding: 0 }}>
                {filteredPrompts.map(p => (
                    <li key={p.id}>
                        <button
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                marginBottom: 4,
                                background: selectedPrompt && selectedPrompt.id === p.id ? '#e0e0e0' : '#fff',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                            onClick={() => setSelectedPrompt(p)}
                        >
                            {p.tag}
                        </button>
                    </li>
                ))}
            </ul>
            {filteredPrompts.length === 0 && searchQuery && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#666' }}>
                No matching prompts found
              </div>
            )}
        </div>
        {/* Editor */}
        <div style={{ width: '70%', padding: 24, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontWeight: 'bold' }}>Prompt Name:</label>
            <input value={editTag} onChange={e => setEditTag(e.target.value)} style={{ fontSize: 18, marginBottom: 12, width: '60%' }} />
            <label style={{ fontWeight: 'bold' }}>Prompt Text:</label>
            <div style={{ flex: 1, minHeight: 0 }}>
              <MdEditor
                style={{ height: 'calc(100vh - 250px)' }}
                value={editFullText}
                renderHTML={text => text}
                onChange={({ text }) => setEditFullText(text)}
                config={{ view: { menu: false, md: true, html: false } }}
              />
            </div>
          </div>
          <div style={{ marginTop: 0, marginBottom: 60, display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
            <button onClick={handleSave} disabled={saving} style={{ fontSize: 18, background: 'green', color: 'white', padding: '8px 24px', border: 'none', borderRadius: 4 }}>Save</button>
            <button onClick={onClose} style={{ fontSize: 18, background: 'orange', color: 'black', padding: '8px 24px', border: 'none', borderRadius: 4 }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptEditor;
